import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Card,
  Tabs,
  Table,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Modal,
  Space,
  Popconfirm,
  Tag,
  Statistic,
  Row,
  Col,
  Switch,
  Collapse,
  Divider,
  Empty,
  Spin,
  Descriptions,
  message,
  Alert
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CalculatorOutlined,
  SwapOutlined,
  ReloadOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import {
  useMultiItemEstimation,
  useAddCategory,
  useUpdateCategory,
  useDeleteCategory,
  useAddLineItem,
  useUpdateLineItem,
  useDeleteLineItem,
  useChangeCostingType,
  useCalculateDetailedCosting,
  useRecalculateEstimation,
  usePatchEstimationOpportunity
} from '../../hooks/useMultiItemEstimation';
import { useCreateQuotationFromBOQ } from '../../hooks/useQuotation';
import { useBOQ } from '../../hooks/useBOQ';
import { useOpportunities, useOpportunity } from '../../hooks/useOpportunities';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { projectService } from '@/services/project.service';
import multiItemEstimationService, {
  CostingType,
  LineItemStatus,
  EstimationLineItem,
  EstimationCategory,
  MultiItemEstimationResponse
} from '../../services/multi-item-estimation.service';
import { documentKeys } from '../../hooks/useDocuments';
import type { Quotation } from '../../services/quotation.service';
import DocumentList from '../../components/documents/DocumentList';
import InheritedAttachmentsCard from '../../components/documents/InheritedAttachmentsCard';
import DocumentUpload from '../../components/documents/DocumentUpload';
import { useAuthStore } from '@/store/authStore';

const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

function getEstimationOpportunityId(
  est: MultiItemEstimationResponse['estimation'] | undefined
): string | undefined {
  if (!est) return undefined;
  return est.opportunityId ?? (est as { opportunity_id?: string }).opportunity_id;
}

function getEstimationOpportunityLabel(est: MultiItemEstimationResponse['estimation'] | undefined) {
  if (!est) return { num: undefined as string | undefined, name: undefined as string | undefined };
  const e = est as {
    opportunityNumber?: string;
    opportunity_number?: string;
    opportunityName?: string;
    opportunity_name?: string;
  };
  return {
    num: e.opportunityNumber ?? e.opportunity_number,
    name: e.opportunityName ?? e.opportunity_name,
  };
}

const MultiItemEstimationBuilderPage: React.FC = () => {
  const { id: estimationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlOpportunityId = searchParams.get('opportunity_id') ?? undefined;
  const [form] = Form.useForm();
  const [categoryForm] = Form.useForm();
  
  // Check if we're creating from BOQ
  const boqData = location.state as any;

  const [activeCategory, setActiveCategory] = useState<string>('');
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [lineItemModalVisible, setLineItemModalVisible] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState<EstimationLineItem | null>(null);
  const [editingCategory, setEditingCategory] = useState<EstimationCategory | null>(null);
  const [crmOppSelect, setCrmOppSelect] = useState<string | undefined>();
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedConvertLineIds, setSelectedConvertLineIds] = useState<string[]>([]);
  const [convertDepartmentId, setConvertDepartmentId] = useState<string | undefined>();
  const [syncingOpportunityDocs, setSyncingOpportunityDocs] = useState(false);
  const queryClient = useQueryClient();

  // Fetch data
  const { data: multiItemData, isLoading, refetch } = useMultiItemEstimation(estimationId || '');
  const { user } = useAuthStore();
  const { data: openOpportunities = [] } = useOpportunities({ status: 'Open' });
  const linkedOppIdForFetch = multiItemData?.estimation
    ? getEstimationOpportunityId(multiItemData.estimation)
    : undefined;
  const { data: linkedOppDetail } = useOpportunity(linkedOppIdForFetch);
  const opportunitySelectOptions = useMemo(() => {
    const list = [...openOpportunities];
    if (linkedOppDetail && !list.some((o) => o.id === linkedOppDetail.id)) {
      list.push(linkedOppDetail);
    }
    return list;
  }, [openOpportunities, linkedOppDetail]);

  // Mutations
  const addCategoryMutation = useAddCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();
  const addLineItemMutation = useAddLineItem();
  const updateLineItemMutation = useUpdateLineItem();
  const deleteLineItemMutation = useDeleteLineItem();
  const changeCostingTypeMutation = useChangeCostingType();
  const calculateDetailedCostingMutation = useCalculateDetailedCosting();
  const recalculateEstimationMutation = useRecalculateEstimation();
  const patchEstimationOpportunityMutation = usePatchEstimationOpportunity();
  const createQuotationFromBoqMutation = useCreateQuotationFromBOQ();
  const sourceBoqId = multiItemData?.estimation?.sourceBoqId;
  const { data: sourceBoq } = useBOQ(sourceBoqId || '', { enabled: !!sourceBoqId });
  const { data: relatedQuotations = [] } = useQuery({
    queryKey: ['quotations', 'by-boq', sourceBoqId],
    queryFn: async () => {
      if (!sourceBoqId) return [] as Quotation[];
      const res = await api.get(`/crm/quotations?boq_id=${encodeURIComponent(sourceBoqId)}`);
      const body = (res as { data?: unknown })?.data ?? res;
      const rows = Array.isArray((body as { data?: unknown })?.data)
        ? ((body as { data: Quotation[] }).data || [])
        : Array.isArray(body)
          ? (body as Quotation[])
          : [];
      return Array.isArray(rows) ? rows : [];
    },
    enabled: !!sourceBoqId,
  });

  useEffect(() => {
    if (!multiItemData?.estimation) return;
    setCrmOppSelect(getEstimationOpportunityId(multiItemData.estimation) ?? urlOpportunityId ?? undefined);
  }, [multiItemData?.estimation, urlOpportunityId, estimationId]);

  // Set initial active category
  useEffect(() => {
    if (multiItemData && multiItemData.categories.length > 0 && !activeCategory) {
      setActiveCategory(multiItemData.categories[0].category.id);
    }
  }, [multiItemData, activeCategory]);

  // Pre-populate form when coming from BOQ
  useEffect(() => {
    if (boqData?.fromBOQ && estimationId === 'new') {
      message.info(`Creating new estimation from BOQ: ${boqData.boqName}`);
    }
  }, [boqData, estimationId]);

  if (!estimationId) {
    return <Empty description="No estimation ID provided" />;
  }
  
  // Handle creating new estimation from BOQ
  if (estimationId === 'new' && boqData?.fromBOQ) {
    return (
      <Card>
        <Alert
          message="Create Estimation from BOQ"
          description={
            <div>
              <p><strong>BOQ:</strong> {boqData.boqName}</p>
              <p><strong>Customer:</strong> {boqData.customerName}</p>
              <p><strong>Project:</strong> {boqData.projectName}</p>
              <p><strong>Currency:</strong> {boqData.currency}</p>
              <p style={{ marginTop: 16 }}>
                To create a multi-item estimation from this BOQ, please follow these steps:
              </p>
              <ol>
                <li>Go to <strong>Estimation Dashboard</strong></li>
                <li>Click <strong>Create New Estimation</strong> and select <strong>Multi-Item Estimation</strong></li>
                <li>Add categories matching your BOQ structure</li>
                <li>For each line item, choose between:
                  <ul>
                    <li><strong>Detailed Costing</strong>: BOM + Operations breakdown</li>
                    <li><strong>Simple Costing</strong>: Quantity × Unit Cost (manual entry)</li>
                  </ul>
                </li>
                <li>Optionally link BOQ items to estimation line items for reference</li>
              </ol>
            </div>
          }
          type="info"
          showIcon
          action={
            <Space direction="vertical">
              <Button type="primary" onClick={() => navigate('/estimation/dashboard')}>
                Go to Estimation Dashboard
              </Button>
              <Button onClick={() => navigate(-1)}>Back to BOQ List</Button>
            </Space>
          }
        />
      </Card>
    );
  }

  // Category Management
  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (category: EstimationCategory) => {
    setEditingCategory(category);
    categoryForm.setFieldsValue({
      categoryName: category.categoryName,
      notes: category.notes
    });
    setCategoryModalVisible(true);
  };

  const handleSaveCategory = async () => {
    try {
      const values = await categoryForm.validateFields();

      if (editingCategory) {
        await updateCategoryMutation.mutateAsync({
          categoryId: editingCategory.id,
          data: values
        });
      } else {
        const sequenceNumber = multiItemData?.categories.length || 0;
        await addCategoryMutation.mutateAsync({
          estimationId,
          data: { ...values, sequenceNumber }
        });
      }

      setCategoryModalVisible(false);
      categoryForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      await deleteCategoryMutation.mutateAsync({ categoryId, deleteLineItems: false });
      if (activeCategory === categoryId && multiItemData && multiItemData.categories.length > 1) {
        const remainingCategories = multiItemData.categories.filter(c => c.category.id !== categoryId);
        if (remainingCategories.length > 0) {
          setActiveCategory(remainingCategories[0].category.id);
        }
      }
      refetch();
    } catch (error) {
      console.error('Failed to delete category:', error);
    }
  };

  // Line Item Management
  const handleAddLineItem = () => {
    setEditingLineItem(null);
    form.resetFields();
    form.setFieldsValue({
      costingType: CostingType.DETAILED,
      quantity: 1,
      marginPercentage: 0,
      leadTimeDays: 0
    });
    setLineItemModalVisible(true);
  };

  const handleEditLineItem = (lineItem: EstimationLineItem) => {
    setEditingLineItem(lineItem);
    form.setFieldsValue({
      itemCode: lineItem.itemCode,
      itemName: lineItem.itemName,
      description: lineItem.description,
      quantity: lineItem.quantity,
      uom: lineItem.uom,
      costingType: lineItem.costingType,
      manualUnitCost: lineItem.manualUnitCost,
      marginPercentage: lineItem.marginPercentage,
      supplierName: lineItem.supplierName,
      leadTimeDays: lineItem.leadTimeDays,
      notes: lineItem.notes
    });
    setLineItemModalVisible(true);
  };

  const handleSaveLineItem = async () => {
    try {
      const values = await form.validateFields();

      if (editingLineItem) {
        await updateLineItemMutation.mutateAsync({
          lineItemId: editingLineItem.id,
          data: values
        });
      } else {
        await addLineItemMutation.mutateAsync({
          estimationId,
          data: {
            ...values,
            categoryId: activeCategory || undefined
          }
        });
      }

      setLineItemModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      console.error('Failed to save line item:', error);
    }
  };

  const handleDeleteLineItem = async (lineItemId: string) => {
    try {
      await deleteLineItemMutation.mutateAsync(lineItemId);
      refetch();
    } catch (error) {
      console.error('Failed to delete line item:', error);
    }
  };

  const handleToggleCostingType = async (lineItem: EstimationLineItem) => {
    const newType = lineItem.costingType === CostingType.SIMPLE ? CostingType.DETAILED : CostingType.SIMPLE;
    try {
      await changeCostingTypeMutation.mutateAsync({
        lineItemId: lineItem.id,
        costingType: newType
      });
      refetch();
    } catch (error) {
      console.error('Failed to change costing type:', error);
    }
  };

  const handleCalculateDetailedCosting = async (lineItemId: string) => {
    try {
      await calculateDetailedCostingMutation.mutateAsync(lineItemId);
      refetch();
      message.success('Detailed costing calculated from BOM and operations');
    } catch (error) {
      console.error('Failed to calculate detailed costing:', error);
    }
  };

  const handleRecalculateEstimation = async () => {
    try {
      await recalculateEstimationMutation.mutateAsync(estimationId);
      refetch();
    } catch (error) {
      console.error('Failed to recalculate estimation:', error);
    }
  };

  // Table columns
  const lineItemColumns = [
    {
      title: '#',
      dataIndex: 'lineNumber',
      key: 'lineNumber',
      width: 60,
      render: (num: number) => <strong>{num}</strong>
    },
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 120
    },
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200,
      render: (text: string, record: EstimationLineItem) => (
        <div>
          <div>{text}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#888' }}>{record.description}</div>
          )}
        </div>
      )
    },
    {
      title: 'Costing Type',
      dataIndex: 'costingType',
      key: 'costingType',
      width: 150,
      render: (type: CostingType, record: EstimationLineItem) => (
        <Space>
          <Tag color={type === CostingType.SIMPLE ? 'blue' : 'green'}>{type}</Tag>
          <Button
            size="small"
            icon={<SwapOutlined />}
            onClick={() => handleToggleCostingType(record)}
            title="Toggle Simple ↔ Detailed"
          />
        </Space>
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (qty: number, record: EstimationLineItem) => `${qty} ${record.uom}`
    },
    {
      title: 'Unit Cost',
      dataIndex: 'totalUnitCost',
      key: 'totalUnitCost',
      width: 120,
      render: (cost: number) => `${currencySymbol} ${Number(cost || 0).toFixed(2)}`
    },
    {
      title: 'Line Total',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 120,
      render: (total: number) => <strong>{currencySymbol} {Number(total || 0).toFixed(2)}</strong>
    },
    {
      title: 'Margin %',
      dataIndex: 'marginPercentage',
      key: 'marginPercentage',
      width: 100,
      render: (margin: number) => `${Number(margin || 0)}%`
    },
    {
      title: 'Selling Price',
      dataIndex: 'lineSellingTotal',
      key: 'lineSellingTotal',
      width: 120,
      render: (price: number) => <strong style={{ color: '#52c41a' }}>{currencySymbol} {Number(price || 0).toFixed(2)}</strong>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: LineItemStatus) => {
        const colors: Record<LineItemStatus, string> = {
          [LineItemStatus.DRAFT]: 'default',
          [LineItemStatus.CALCULATED]: 'processing',
          [LineItemStatus.APPROVED]: 'success'
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: EstimationLineItem) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditLineItem(record)}
          />
          {record.costingType === CostingType.DETAILED && (
            <Button
              size="small"
              icon={<CalculatorOutlined />}
              onClick={() => handleCalculateDetailedCosting(record.id)}
              title="Calculate from BOM & Operations"
            />
          )}
          <Popconfirm
            title="Delete this line item?"
            onConfirm={() => handleDeleteLineItem(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Render detailed cost breakdown
  const renderDetailedCostBreakdown = (lineItem: EstimationLineItem) => {
    if (lineItem.costingType !== CostingType.DETAILED) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#888' }}>
          <p>Simple costing mode - using manual unit cost</p>
          <p><strong>{currencySymbol} {Number(lineItem.manualUnitCost || 0).toFixed(2)}</strong> per unit</p>
        </div>
      );
    }

    return (
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Material Cost">{currencySymbol} {Number(lineItem.materialCost || 0).toFixed(2)}</Descriptions.Item>
        <Descriptions.Item label="Labor Cost">{currencySymbol} {Number(lineItem.laborCost || 0).toFixed(2)}</Descriptions.Item>
        <Descriptions.Item label="Overhead Cost">{currencySymbol} {Number(lineItem.overheadCost || 0).toFixed(2)}</Descriptions.Item>
        <Descriptions.Item label="Operation Cost">{currencySymbol} {Number(lineItem.operationCost || 0).toFixed(2)}</Descriptions.Item>
        <Descriptions.Item label="Tooling Cost">{currencySymbol} {Number(lineItem.toolingCost || 0).toFixed(2)}</Descriptions.Item>
        <Descriptions.Item label={<strong>Total Unit Cost</strong>}>
          <strong>{currencySymbol} {Number(lineItem.totalUnitCost || 0).toFixed(2)}</strong>
        </Descriptions.Item>
      </Descriptions>
    );
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" tip="Loading estimation..." />
      </div>
    );
  }

  const activeCategoryData = multiItemData?.categories.find(c => c.category.id === activeCategory);
  const categoryTotal = Number(activeCategoryData?.category.categoryTotal || 0);

  // Get currency from estimation data
  const currency = multiItemData?.estimation?.currency || 'USD';
  const currencySymbol = currency === 'AED' ? 'AED' : currency === 'USD' ? '$' : currency;

  const linkedOppId = multiItemData?.estimation
    ? getEstimationOpportunityId(multiItemData.estimation)
    : undefined;
  const linkedOppLabel = multiItemData?.estimation
    ? getEstimationOpportunityLabel(multiItemData.estimation)
    : { num: undefined as string | undefined, name: undefined as string | undefined };
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const userPerms = user?.permissions || [];
  const canConvertBoqToQuotation =
    isSuperAdmin || userPerms.includes('crm.quotation.create') || userPerms.includes('growth.view');
  const boqStatusAllowsConversion = ['Approved', 'Converted'].includes(String(sourceBoq?.status || ''));
  const convertLineCandidates = sourceBoq?.lineItems || [];
  const lineIdsAlreadyQuoted = (() => {
    const ids = new Set<string>();
    for (const q of relatedQuotations || []) {
      for (const item of q.items || []) {
        const lineId = String(item.boq_line_item_id || '').trim();
        if (lineId) ids.add(lineId);
      }
    }
    return ids;
  })();
  const handleOpenConvertModal = async () => {
    if (!sourceBoqId || !sourceBoq?.lineItems) return;
    setSelectedConvertLineIds(sourceBoq.lineItems.map((line) => line.id));
    setConvertDepartmentId(undefined);
    setConvertModalOpen(true);
    const projectId = sourceBoq.project_id ? String(sourceBoq.project_id).trim() : '';
    if (projectId) {
      try {
        const project = await projectService.getProjectById(projectId);
        const deptId = project?.department_id ? String(project.department_id).trim() : '';
        if (deptId) setConvertDepartmentId(deptId);
      } catch {
        // Prefill is best-effort; user can still pick department in the modal.
      }
    }
  };
  const handleConvertSelectedLines = async () => {
    if (!sourceBoqId) return;
    if (selectedConvertLineIds.length === 0) {
      message.warning('Select at least one BOQ line item.');
      return;
    }
    if (!convertDepartmentId?.trim()) {
      message.warning('Department is required');
      return;
    }
    try {
      const res = await createQuotationFromBoqMutation.mutateAsync({
        boqId: sourceBoqId,
        opportunityId: linkedOppId,
        selectedLineIds: selectedConvertLineIds,
        departmentId: convertDepartmentId.trim(),
      });
      const quotationId = ((res as { data?: { id?: string }; id?: string })?.data?.id ||
        (res as { id?: string })?.id) as string | undefined;
      message.success('Quotation created from selected BOQ lines.');
      setConvertModalOpen(false);
      if (quotationId) {
        navigate(`/growth/quotations/${quotationId}`);
      }
      refetch();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create quotation');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Card>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <h2>Multi-Item Estimation Builder</h2>
            <p style={{ margin: 0, color: '#888' }}>Estimation ID: {estimationId}</p>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleRecalculateEstimation}>
                Recalculate All
              </Button>
              {sourceBoqId &&
                canConvertBoqToQuotation &&
                boqStatusAllowsConversion &&
                convertLineCandidates.length > 0 && (
                  <Button icon={<CheckCircleOutlined />} onClick={handleOpenConvertModal}>
                    Convert to Quotation
                  </Button>
                )}
              <Button type="primary" icon={<SaveOutlined />}>
                Save Estimation
              </Button>
              <Button onClick={() => navigate('/estimation/dashboard')}>
                Back to Dashboard
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {estimationId && estimationId !== 'new' && multiItemData && (
        <Card title="CRM link" style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              {linkedOppId ? (
                <Space wrap>
                  <span>
                    <strong>{linkedOppLabel.num || '—'}</strong>
                    {linkedOppLabel.name ? ` — ${linkedOppLabel.name}` : ''}
                  </span>
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/growth/opportunities/${linkedOppId}`)}
                  >
                    Open opportunity
                  </Button>
                </Space>
              ) : (
                <span style={{ color: '#888' }}>No CRM opportunity linked.</span>
              )}
            </div>
            <Space wrap align="start">
              <Select
                style={{ minWidth: 280 }}
                placeholder="Select opportunity"
                showSearch
                allowClear
                optionFilterProp="label"
                value={crmOppSelect}
                onChange={(v) => setCrmOppSelect(v)}
                options={opportunitySelectOptions.map((o) => ({
                  value: o.id,
                  label: `${o.opportunity_number} — ${o.opportunity_name}`,
                }))}
              />
              <Button
                type="primary"
                loading={patchEstimationOpportunityMutation.isPending}
                onClick={async () => {
                  try {
                    await patchEstimationOpportunityMutation.mutateAsync({
                      estimationId: estimationId!,
                      opportunity_id: crmOppSelect ?? null,
                    });
                    message.success('CRM link saved');
                    refetch();
                  } catch {
                    /* error toast from hook */
                  }
                }}
              >
                Save link
              </Button>
              <Button
                disabled={!linkedOppId}
                loading={patchEstimationOpportunityMutation.isPending}
                onClick={async () => {
                  try {
                    await patchEstimationOpportunityMutation.mutateAsync({
                      estimationId: estimationId!,
                      opportunity_id: null,
                    });
                    setCrmOppSelect(undefined);
                    message.success('CRM link removed');
                    refetch();
                  } catch {
                    /* error toast from hook */
                  }
                }}
              >
                Remove link
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      {sourceBoqId && (
        <Card title={`Related Quotations (${relatedQuotations.length})`} style={{ marginTop: 16 }}>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            locale={{ emptyText: 'No quotations created from this BOQ yet.' }}
            dataSource={relatedQuotations}
            columns={[
              {
                title: 'Quotation',
                key: 'quotation_number',
                render: (_, record) => (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/growth/quotations/${record.id}`)}
                  >
                    {record.quotation_number}
                  </Button>
                ),
              },
              {
                title: 'Date',
                key: 'quotation_date',
                render: (_, record) =>
                  record.quotation_date ? new Date(record.quotation_date).toISOString().slice(0, 10) : '—',
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
              },
              {
                title: 'Selected lines',
                key: 'selected_lines',
                render: (_, record) =>
                  (record.items || []).filter((item) => Boolean(item.boq_line_item_id)).length,
              },
              {
                title: 'Value',
                key: 'value',
                render: (_, record) => `${currencySymbol} ${Number(record.grand_total || 0).toFixed(2)}`,
              },
            ]}
          />
        </Card>
      )}

      {/* Summary Statistics */}
      {multiItemData && (
        <Card style={{ marginTop: '16px' }}>
          <Row gutter={16}>
            <Col span={4}>
              <Statistic
                title="Total Categories"
                value={multiItemData.summary.totalCategories}
                prefix={<BarChartOutlined />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Total Line Items"
                value={multiItemData.summary.totalLineItems}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Simple Items"
                value={multiItemData.summary.simpleItems}
                valueStyle={{ color: '#145A54' }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Detailed Items"
                value={multiItemData.summary.detailedItems}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Total Cost"
                value={multiItemData.summary.totalCost}
                precision={2}
                prefix={currencySymbol}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Selling Price"
                value={multiItemData.summary.totalSellingPrice}
                precision={2}
                prefix={currencySymbol}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Documents Section */}
      {estimationId && estimationId !== 'new' && (
        <Card
          title="Documents"
          style={{ marginTop: '16px' }}
          extra={
            <Button
              type="primary"
              icon={<SyncOutlined />}
              disabled={!linkedOppId}
              loading={syncingOpportunityDocs}
              title={linkedOppId ? undefined : 'Link an opportunity to sync documents'}
              onClick={async () => {
                setSyncingOpportunityDocs(true);
                try {
                  const r = await multiItemEstimationService.syncOpportunityDocuments(estimationId);
                  message.success(
                    r.copied > 0
                      ? `Copied ${r.copied} document(s) from the opportunity`
                      : 'No new documents to copy from the opportunity'
                  );
                  await queryClient.invalidateQueries({
                    queryKey: documentKeys.byEntity('Estimation', estimationId),
                  });
                } catch (e: any) {
                  message.error(e?.response?.data?.message || e?.message || 'Sync failed');
                } finally {
                  setSyncingOpportunityDocs(false);
                }
              }}
            >
              Sync documents from opportunity
            </Button>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <DocumentUpload
              entityType="Estimation"
              entityId={estimationId}
              onSuccess={() => {
                // Refetch will be handled by DocumentList component
              }}
            />
            <DocumentList entityType="Estimation" entityId={estimationId} />
          </Space>
          <InheritedAttachmentsCard trailDomain="sales" entityType="Estimation" entityId={estimationId} />
        </Card>
      )}

      {/* Category Tabs */}
      <Card style={{ marginTop: '16px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCategory}>
            Add Category
          </Button>
        </div>

        {multiItemData && multiItemData.categories.length > 0 ? (
          <Tabs
            activeKey={activeCategory}
            onChange={setActiveCategory}
            type="card"
            tabBarExtraContent={
              activeCategoryData && (
                <Space>
                  <Tag color="blue">
                    Category Total: {currencySymbol} {categoryTotal.toFixed(2)}
                  </Tag>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditCategory(activeCategoryData.category)}
                  >
                    Edit Category
                  </Button>
                  <Popconfirm
                    title="Delete this category?"
                    description="Line items will be moved to uncategorized."
                    onConfirm={() => handleDeleteCategory(activeCategoryData.category.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      Delete Category
                    </Button>
                  </Popconfirm>
                </Space>
              )
            }
          >
            {multiItemData.categories.map(({ category, lineItems }) => (
              <Tabs.TabPane
                tab={`${category.categoryName} (${lineItems.length})`}
                key={category.id}
              >
                <div style={{ marginBottom: '16px' }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddLineItem}>
                    Add Line Item
                  </Button>
                </div>

                <Table
                  columns={lineItemColumns}
                  dataSource={lineItems}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: 1500 }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <div style={{ padding: '16px', background: '#fafafa' }}>
                        <h4>Cost Breakdown</h4>
                        {renderDetailedCostBreakdown(record)}
                        {record.notes && (
                          <>
                            <Divider />
                            <p><strong>Notes:</strong> {record.notes}</p>
                          </>
                        )}
                      </div>
                    ),
                    rowExpandable: () => true
                  }}
                />
              </Tabs.TabPane>
            ))}
          </Tabs>
        ) : (
          <Empty
            description="No categories yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCategory}>
              Create First Category
            </Button>
          </Empty>
        )}
      </Card>

      <Modal
        title="Create Quotation from BOQ"
        open={convertModalOpen}
        width={980}
        onCancel={() => setConvertModalOpen(false)}
        onOk={handleConvertSelectedLines}
        okText="Create Quotation"
        okButtonProps={{ loading: createQuotationFromBoqMutation.isPending }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="BOQ">
              {sourceBoq?.boq_number || multiItemData?.estimation?.sourceBoqNumber || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">{sourceBoq?.status || '—'}</Descriptions.Item>
            <Descriptions.Item label="Customer">{sourceBoq?.customer_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Opportunity">{linkedOppLabel.num || '—'}</Descriptions.Item>
            <Descriptions.Item label="Project">{sourceBoq?.project_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="BOQ Value">
              {currencySymbol} {Number(sourceBoq?.selling_price || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Existing quotations">
              {relatedQuotations.length}
            </Descriptions.Item>
            <Descriptions.Item label="Selected lines">{selectedConvertLineIds.length}</Descriptions.Item>
          </Descriptions>

          <div>
            <strong>
              Department <span style={{ color: '#ff4d4f' }}>*</span>
            </strong>
            <div style={{ marginTop: 8 }}>
              <DepartmentSelect
                value={convertDepartmentId}
                onChange={(value) => setConvertDepartmentId(value || undefined)}
                style={{ width: '100%' }}
                placeholder="Select department"
              />
            </div>
          </div>

          <Space>
            <Button onClick={() => setSelectedConvertLineIds(convertLineCandidates.map((line) => line.id))}>
              Select all
            </Button>
            <Button onClick={() => setSelectedConvertLineIds([])}>Deselect all</Button>
          </Space>

          <Table
            size="small"
            rowKey="id"
            pagination={{ pageSize: 8 }}
            dataSource={convertLineCandidates}
            rowSelection={{
              selectedRowKeys: selectedConvertLineIds,
              onChange: (keys) => setSelectedConvertLineIds(keys.map((k) => String(k))),
            }}
            columns={[
              { title: '#', dataIndex: 'line_number', key: 'line_number', width: 60 },
              { title: 'Item', dataIndex: 'item_name', key: 'item_name', ellipsis: true },
              { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
              { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 100 },
              { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80 },
              {
                title: 'Unit Rate',
                key: 'unit_rate',
                render: (_, row) => `${currencySymbol} ${Number(row.unit_selling_price || row.unit_cost || 0).toFixed(2)}`,
              },
              {
                title: 'Amount',
                key: 'amount',
                render: (_, row) => `${currencySymbol} ${Number(row.line_selling_total || row.line_total || 0).toFixed(2)}`,
              },
              {
                title: 'Already quoted',
                key: 'already_quoted',
                render: (_, row) => (lineIdsAlreadyQuoted.has(row.id) ? <Tag color="gold">Yes</Tag> : '—'),
              },
            ]}
          />
        </Space>
      </Modal>

      {/* Category Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        open={categoryModalVisible}
        onOk={handleSaveCategory}
        onCancel={() => setCategoryModalVisible(false)}
        confirmLoading={addCategoryMutation.isPending || updateCategoryMutation.isPending}
      >
        <Form form={categoryForm} layout="vertical">
          <Form.Item
            name="categoryName"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input placeholder="e.g., Materials, Labor, Equipment" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Optional notes about this category" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Line Item Modal */}
      <Modal
        title={editingLineItem ? 'Edit Line Item' : 'Add Line Item'}
        open={lineItemModalVisible}
        onOk={handleSaveLineItem}
        onCancel={() => setLineItemModalVisible(false)}
        confirmLoading={addLineItemMutation.isPending || updateLineItemMutation.isPending}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="itemCode" label="Item Code">
                <Input placeholder="Optional item code" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="itemName"
                label="Item Name"
                rules={[{ required: true, message: 'Please enter item name' }]}
              >
                <Input placeholder="Item name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="uom"
                label="Unit of Measure"
                rules={[{ required: true, message: 'Please enter UOM' }]}
              >
                <Input placeholder="e.g., pcs, kg, hrs" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="costingType"
                label="Costing Type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value={CostingType.SIMPLE}>Simple</Option>
                  <Option value={CostingType.DETAILED}>Detailed</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.costingType !== currentValues.costingType
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('costingType') === CostingType.SIMPLE ? (
                <Form.Item
                  name="manualUnitCost"
                  label="Manual Unit Cost"
                  rules={[{ required: true, message: 'Please enter unit cost' }]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix={currencySymbol}
                    style={{ width: '100%' }}
                    placeholder="Unit cost"
                  />
                </Form.Item>
              ) : (
                <Alert
                  message="Detailed Costing"
                  description="Costs will be calculated from BOM items and operations. Add them after creating this line item."
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )
            }
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="marginPercentage" label="Margin %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="leadTimeDays" label="Lead Time (Days)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="supplierName" label="Supplier Name">
            <Input placeholder="Optional supplier" />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Optional notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MultiItemEstimationBuilderPage;

